import React, { useEffect, useState } from 'react';
// import './orderBook.css';
import {
  Row,
  Col,
  Select,
  Input,
  DatePicker,
  Form,
  Table,
  Typography,
  Modal,
  Button,
  Space,
  Badge,
  Tooltip,
} from 'antd';
import { ExclamationCircleFilled, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router';
import orderBookService from '../../services/OrderBookService';
import { useAuth } from '../../hooks/useAuth';
import { convertToLocal } from '../../components/Functions/convertToLocal';
import Checkbox from 'antd/es/checkbox/Checkbox';
import { objectToQueryString } from '../../util/Common';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

const OrderBook = () => {
  // Form control
  const [form] = Form.useForm();
  const [requiredMark, setRequiredMarkType] = useState('');
  const [orderList, setOrderList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showForm, setShowForm] = useState(true);
  const [isChecked, setIsChecked] = useState(true);
  // const [page, setPage] = useState(1);
  // const [size, setSize] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useAuth();

  const queryParams = new URLSearchParams(location.search);

  const startTimestamp = Number(queryParams.get('startDate')) || null;
  const endTimestamp = Number(queryParams.get('endDate')) || null;
  const tradingAccountNo = queryParams.get('tradingAccountNo') || null;
  const symbolCode = queryParams.get('symbolCode') || null;
  const orderSideType = queryParams.get('orderSideType') || null;
  const orderPriceType = queryParams.get('orderPriceType') || null;
  const orderExecutionStateTypeList = queryParams.get('orderExecutionStateTypeList')
    ? queryParams.get('orderExecutionStateTypeList').split(',')
    : ['INITIATED', 'SUBMITTED', 'ACCEPTED'];
  const orderRejectionStateType = queryParams.get('orderRejectionStateType') || null;

  const page = Number(queryParams.get('page')) || 1;
  const size = Number(queryParams.get('size')) || 10;

  const startDate = startTimestamp ? dayjs(startTimestamp * 1000) : null;
  const endDate = endTimestamp ? dayjs(endTimestamp * 1000) : null;

  const [intervalTime, setIntervalTime] = useState(10000); // Default interval of 1 second

  const onRequiredTypeChange = ({ requiredMarkValue }) => {
    setRequiredMarkType(requiredMarkValue);
  };

  const loadData = async () => {
    const payload = {
      tradingAccountNo: tradingAccountNo,
      startDate: startTimestamp,
      endDate: endTimestamp,
      symbolCode: symbolCode,
      orderSideType: orderSideType,
      orderPriceType: orderPriceType,
      orderExecutionStateTypeList: orderExecutionStateTypeList,
      orderRejectionStateType: orderRejectionStateType,
      page: page,
      size: size,
    };

    const response = await orderBookService.getOrderBook(
      payload,
      // ? payload
      // : {
      //     page: pageParam,
      //     size: sizeParam,
      //   },
    );

    if (response.success === true) {
      setOrderList(response.orderList);
      setTotalCount(response.totalCount);
    } else {
      showNotification('error', response.message);
    }
  };

  useEffect(() => {
    loadData();
  }, [location.search]);

  useEffect(() => {
    // Schedule a refresh based on the selected interval
    const intervalId = setInterval(() => {
      if (isChecked) {
        loadData();
      }
    }, intervalTime);

    // Clean up the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, [intervalTime, isChecked, queryParams]);

  // Table columns
  const tableColumns = [
    {
      title: 'Trade Order ID',
      dataIndex: 'tradeOrderId',
      key: 'tradeOrderId',
      fixed: 'left',
    },
    {
      title: 'Trading Account No.',
      dataIndex: 'tradingAccountNo',
      key: 'tradingAccountNo',
      fixed: 'left',
    },
    {
      title: 'Security Code',
      dataIndex: 'securityCode',
      key: 'securityCode',
      fixed: 'left',
    },
    {
      title: 'Symbol Code',
      dataIndex: 'symbolCode',
      key: 'symbolCode',
    },
    // {
    //   title: 'Cancellation Order ID',
    //   dataIndex: 'cancellationOrderId',
    //   key: 'cancellationOrderId',
    //   // removed duplicates first
    //   //   filters: [...new Set(applicationList.map(item => item.eyeColor))].map(item => {
    //   //     return {
    //   //       text: item,
    //   //       value: item,
    //   //     };
    //   //   }),
    //   //   onFilter: (value, record) => record.eyeColor.startsWith(value),
    // },
    {
      title: 'Order Price Type',
      dataIndex: 'orderPriceType',
      key: 'orderPriceType',
    },
    {
      title: 'Order Side Type',
      dataIndex: 'orderSideType',
      key: 'orderSideType',
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
    },
    {
      title: 'Initiated Quantity',
      dataIndex: 'initiatedQuantity',
      key: 'initiatedQuantity',
      align: 'right',
      render: text => Number(text).toLocaleString('en-US'),
    },
    {
      title: 'Initiated Price',
      dataIndex: 'initiatedPrice',
      key: 'initiatedPrice',
      align: 'right',
      render: text => Number(text).toLocaleString('en-US'),
    },
    // {
    //   title: 'Pre Order',
    //   dataIndex: 'preOrder',
    //   key: 'preOrder',
    //   align: 'center',
    //   render: text => <>{true ? <Badge status="error" text="" /> : 'false'}</>,
    // },
    {
      title: 'Trade Window',
      dataIndex: 'tradeWindow',
      key: 'tradeWindow',
      align: 'right',
    },
    // {
    //   title: 'Order Initiator Type',
    //   dataIndex: 'orderInitiatorType',
    //   key: 'orderInitiatorType',
    // },
    // {
    //   title: 'Order Initiator ID',
    //   dataIndex: 'orderInitiatorId',
    //   key: 'orderInitiatorId',
    // },
    // {
    //   title: 'Executed Quantity',
    //   dataIndex: 'executedQuantity',
    //   key: 'executedQuantity',
    // },
    {
      title: 'Execution State',
      dataIndex: 'orderExecutionStateType',
      key: 'orderExecutionStateType',
      align: 'right',
    },
    // {
    //   title: 'Cancellation State',
    //   dataIndex: 'orderCancellationStateType',
    //   key: 'orderCancellationStateType',
    // },
    {
      title: 'Expiry State',
      dataIndex: 'orderExpiryStateType',
      key: 'orderExpiryStateType',
      align: 'right',
    },
    {
      title: 'Rejection State',
      dataIndex: 'orderRejectionStateType',
      key: 'orderRejectionStateType',
      align: 'right',
    },
    {
      title: 'Reject Reason',
      dataIndex: 'rejectReason',
      key: 'rejectReason',
      align: 'right',
    },
    {
      title: 'Initiated At',
      dataIndex: 'initiatedAt',
      key: 'initiatedAt',
      align: 'right',
      render: (_, record) => convertToLocal(record.initiatedAt),
    },
    {
      title: 'Submitted At',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      align: 'right',
      render: (_, record) => convertToLocal(record.submittedAt),
    },
    {
      title: 'Accepted At',
      dataIndex: 'acceptedAt',
      key: 'acceptedAt',
      align: 'right',
      render: (_, record) => convertToLocal(record.acceptedAt),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <>
          <Button
            // icon={
            //   <Image src={`${process.env.PUBLIC_URL}/images/delete-icon.png`} preview={false} />
            // }
            type={'default'}
            size="small"
            danger
            onClick={() => showDeleteConfirm(record)}
          >
            Cancel
          </Button>
        </>
      ),
    },
    // {
    //   title: 'Accepted At',
    //   dataIndex: 'acceptedAt',
    //   key: 'acceptedAt',
    // },
    // {
    //   title: 'Fulfilled At',
    //   dataIndex: 'fulfilledAt',
    //   key: 'fulfilledAt',
    // },
  ];

  const onFinish = async formValues => {
    let startDateTimestamp = null;
    let endDateTimestamp = null;
  
    if (formValues.selectDates && formValues.selectDates[0]) {
      startDateTimestamp = formValues.selectDates[0].startOf('day').valueOf(); 
    }
    if (formValues.selectDates && formValues.selectDates[1]) {

      endDateTimestamp = formValues.selectDates[1].endOf('day').valueOf(); 
    }

    let payload = {
      tradingAccountNo: formValues.tradingAccountNumber ? formValues.tradingAccountNumber : null,
      symbolCode: formValues.symbolCode ? formValues.symbolCode : null,
      orderPriceType: formValues.orderPriceType ? formValues.orderPriceType : null,
      orderSideType: formValues.orderSideType ? formValues.orderSideType : null,
      orderExecutionStateTypeList: formValues.orderExecutionState ? formValues.orderExecutionState : null,
      orderRejectionStateType: formValues.orderRejectionStateType
        ? formValues.orderRejectionStateType
        : null,
      startDate: startDateTimestamp !== null ? Math.floor(startDateTimestamp / 1000) : null,
      endDate: endDateTimestamp !== null ? Math.floor(endDateTimestamp / 1000) : null,
      page: 1,
      size: 10,
    };

    console.log(payload);

    // loadData(payload);
    const queryString = objectToQueryString(payload);
    navigate('/trading/order-book?' + queryString);
    // form.setFieldsValue({
    //   tradingAccountNumber: null,
    //   symbolCode: null,
    //   orderPriceType: null,
    //   orderSideType: null,
    //   // orderExecutionState: null,
    //   orderRejectionStateType: null,
    // });
  };

  const handleIntervalChange = value => {
    setIntervalTime(value);
  };

  const showDeleteConfirm = record => {
    confirm({
      title: 'Warning',
      icon: <ExclamationCircleFilled />,
      content: 'Are you sure to cancel this item?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        handleCancel(record);
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleCancel = async record => {
    console.log(record.tradeOrderId);
    const payload = {
      tradeOrderId: record.tradeOrderId,
    };
    const result = await orderBookService.cancelOrder(payload);
    console.log(result);
    if (result.success) {
      showNotification('success', `Deleted Order ID - ${record.tradeOrderId}`);
      loadData();
    } else {
      showNotification('error', result.message);
    }
  };

  const handleRefresh = () => {
    navigate('/trading/order-book');
    form.setFieldsValue({
      tradingAccountNumber: null,
      selectDates: [null, null],
      symbolCode: null,
      orderSideType: null,
      orderPriceType: null,
      orderExecutionState: ['INITIATED', 'SUBMITTED', 'ACCEPTED'],
      orderRejectionStateType: null,
    });
  };

  return (
    <>
      <section
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          // marginBottom: '2rem',x
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Order Book
        </Title>
        <Checkbox
          defaultChecked={isChecked}
          checked={isChecked}
          onChange={e => setIsChecked(e.target.checked)}
        >
          Auto Refresh
        </Checkbox>
      </section>
      {/* <Space
        style={{
          justifyContent: 'flex-end',
          display: 'flex',
          margin: '20px 0px',
        }}
      > */}
      {/* <Button
            type="default"
            size={'middle'}
            icon={<FilterOutlined />}
            onClick={() => setShowForm(showForm => (showForm = !showForm))}
          >
            Filter
          </Button> */}
      {/* </Space> */}
      <Space
        style={{
          justifyContent: 'flex-end',
          display: 'flex',
          margin: '20px 0px',
        }}
      >
        <Select defaultValue={5000} onChange={handleIntervalChange} disabled={!isChecked}>
          <Select.Option value={5000}>5 seconds</Select.Option>
          <Select.Option value={30000}>30 seconds</Select.Option>
          <Select.Option value={50000}>50 seconds</Select.Option>
        </Select>
        <Button
          type="default"
          size={'middle'}
          icon={<FilterOutlined />}
          onClick={() => setShowForm(showForm => (showForm = !showForm))}
        >
          Filter
        </Button>
      </Space>
      <Form
        onFinish={onFinish}
        form={form}
        labelCol={{
          span: 8,
          lg: {
            span: 12,
          },
          xl: {
            span: 12,
          },
        }}
        onValuesChange={onRequiredTypeChange}
        requiredMark={requiredMark}
        hidden={showForm}
        initialValues={{
          tradingAccountNumber: tradingAccountNo,
          selectDates: [startDate, endDate],
          symbolCode: symbolCode,
          orderSideType: orderSideType,
          orderPriceType: orderPriceType,
          orderExecutionState: orderExecutionStateTypeList,
          orderRejectionStateType: orderRejectionStateType,
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={24} md={24} lg={12} xl={8}>
            <Form.Item label="Trading Account No" name={'tradingAccountNumber'}>
              <Input size="middle" />
            </Form.Item>

            <Form.Item label="Symbol Code" name={'symbolCode'}>
              <Input size="middle" />
            </Form.Item>
            <Form.Item label="Order Execution State Type" name={'orderExecutionState'}>
              <Select
                mode="multiple"
                size="middle"
                // defaultValue={['INITIATED', 'SUBMITTED', 'ACCEPTED']}
              >
                <Select.Option value="INITIATED">INITIATED</Select.Option>
                <Select.Option value="SUBMITTED">SUBMITTED</Select.Option>
                <Select.Option value="ACCEPTED">ACCEPTED</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={24} lg={12} xl={8}>
            <Form.Item label="Order Side Type" name={'orderSideType'}>
              <Select size="middle">
                <Select.Option value="BUY">BUY</Select.Option>
                <Select.Option value="SELL">SELL</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Order Price Type" name={'orderPriceType'}>
              <Select size="middle">
                <Select.Option value="MARKET">MARKET</Select.Option>
                <Select.Option value="LIMIT">LIMIT</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={8}>
            <Form.Item label="Rejection Type" name={'orderRejectionStateType'}>
              <Select size="middle">
                <Select.Option value="NONE">NONE</Select.Option>
                <Select.Option value="REJECTED">REJECTED</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="Select Dates" name={'selectDates'}>
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Space style={{ float: 'right', margin: '20px 0px' }} align="center">
          <Tooltip title="Clear fields">
            <Button
              type="default"
              size={'middle'}
              icon={<ClearOutlined />}
              onClick={handleRefresh}
            ></Button>
          </Tooltip>
          {/* <Form.Item> */}
          <Button
            type="primary"
            size={'middle'}
            htmlType="submit"
            key="submit"
            style={{ marginBottom: '0px' }}
            // icon={<SearchOutlined />}
          >
            Search
          </Button>
          {/* </Form.Item> */}
        </Space>
      </Form>
      <Table
        columns={tableColumns}
        dataSource={orderList && orderList.map(order => ({ ...order, key: order.tradeOrderId }))}
        size={'large'}
        scroll={{ x: 'max-content' }}
        rowClassName={record => (record.preOrder ? 'custom-colored-row' : '')}
        bordered
        pagination={{
          current: page,
          pageSize: size,
          total: totalCount,
        }}
        onChange={(pagination, filters, sorter) => {
          const payload = {
            tradingAccountNo: tradingAccountNo,
            startDate: startTimestamp,
            endDate: endTimestamp,
            symbolCode: symbolCode,
            orderSideType: orderSideType,
            orderPriceType: orderPriceType,
            orderExecutionStateTypeList: orderExecutionStateTypeList,
            orderRejectionStateType: orderRejectionStateType,
            page: pagination.current,
            size: pagination.pageSize,
          };
          const queryString = objectToQueryString(payload);
          navigate('/trading/order-book?' + queryString);
        }}
      />
    </>
  );
};

export default OrderBook;
